import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { translate } from '../../../i18n';
import { GAME_QUERY_KEY } from './useGetGame';

interface ChargePayLaterBookingResponse {
    success: boolean;
    paymentIntentId: string;
    status: string;
    amount: number;
    currency: string;
    message: string;
}

interface ChargePayLaterBookingRequest {
    paymentMethodId?: string;
    force?: boolean;
    customAmount?: number; // Amount in cents
}

export const useChargePayLaterBooking = () => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const apiClient = useApiClient();
    return useMutation({
        mutationFn: async ({
            paymentIntentId,
            options = {}
        }: {
            paymentIntentId: string;
            options?: ChargePayLaterBookingRequest
        }) => {
            return apiClient.fetchApi<ChargePayLaterBookingResponse, ChargePayLaterBookingRequest>({
                endpoint: `payments/charge-pay-later-booking/${paymentIntentId}`,
                method: 'POST',
                body: options,
            });
        },
        onSuccess: (data, variables) => {
            if (data.success) {
                toast({
                    title: translate('game.payment_charged_successfully'),
                    description: data.message,
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });

                queryClient.invalidateQueries({ queryKey: ['game'] });
            } else {
                toast({
                    title: translate('game.payment_failed'),
                    description: data.message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        },
        onError: (error: any) => {
            toast({
                title: translate('game.error_charging_payment'),
                description: error?.message || translate('common.unexpected_error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });
};